import React, { useState, useEffect, useContext } from "react";
import {
  getAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken,
  GoogleAuthProvider,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import "./SignIn.css";
import { doc, setDoc } from "firebase/firestore";
import { nanoid } from "nanoid";
import { EnvContext, collectionPath } from "../../context/EnvContext";
import { UserContext } from "../../context/UserContext";
import { db, logAnalyticsEvent } from "../../firebase/firebaseConfig";
import { useModal } from "../../context/ModalContext";
import addToCart from "../../functions/addToCart";
import useCart from "../../hooks/useCart";
import apiClient from "../../utils/ApiClient";
import { CircularProgress } from "@mui/material";

const breakpoints = [755];
const mq = breakpoints.map((bp) => `@media (min-width: ${bp}px)`);

const SignIn = () => {
  const environment = useContext(EnvContext);
  const { user } = useContext(UserContext);
  const { cartItems } = useCart();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [token, setToken] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [extendedLoading, setExtendedLoading] = useState(false);
  const [error, setError] = useState("");
  const { currentModal, setCurrentModal, modalCallback } = useModal();

  const auth = getAuth();

  useEffect(() => {
    if (user?.email && !currentModal) {
      navigate("/");
    }
  }, [currentModal, user?.email, navigate]);

  // Analytics
  useEffect(() => {
    // analytics
    logAnalyticsEvent("sign_in_view", {
      page_title:
        environment === "staging" ? "STAGING - Sign In Page" : "Sign In Page",
      page_location: window.location.href,
      page_path: window.location.pathname,
      environment: environment,
    });
  }, [environment]);

  useEffect(() => {
    let timer;
    if (loading) {
      timer = setTimeout(() => {
        setExtendedLoading(true);
      }, 3000);
    } else {
      setExtendedLoading(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [loading]);

  const createUserInFirestore = async (user) => {
    const firestore = db;
    const userDocRef = doc(firestore, "Users/UsersList/DataBase", user.uid);

    await setDoc(
      userDocRef,
      {
        uid: user.uid,
        email: user.email,
        isAnonymous: false,
        createdAt: new Date(),
        user_code: nanoid(6),
      },
      { merge: true },
    );
  };

  const recreateCartForUser = async (newUser, items) => {
    if (items.length === 0) return;

    try {
      // Add each item from the guest cart to the new user's cart
      for (const item of items) {
        await addToCart(item.id, newUser, db, item.quantity, collectionPath, {
          overrideQuantity: true,
        });
      }

      // Call the callback to refresh the cart in Checkout component
      if (modalCallback) {
        modalCallback();
      }
    } catch (error) {
      console.error("Error transferring cart items:", error);
    }
  };

  const handleRequestOtp = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    try {
      const data = await apiClient.postData("/auth/request-otp", { email });
      setToken(data.token);
      setStep(2);
    } catch (error) {
      setError(error.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await apiClient.postData("/auth/request-otp", { email });
      setToken(data.token);
    } catch (error) {
      setError(error.message || "Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    try {
      const data = await apiClient.postData("/auth/verify-otp", { token, otp });

      if (!data.customToken) {
        throw new Error("No custom token received from server");
      }

      const oldCartItems = cartItems;
      const result = await signInWithCustomToken(auth, data.customToken);

      if (!result || !result.user) {
        throw new Error("Authentication failed - no user returned");
      }

      await recreateCartForUser(result.user, oldCartItems);
      setCurrentModal(null);

      logAnalyticsEvent("sign_in_mail", {
        method: "otp",
        environment: environment,
      });
    } catch (error) {
      console.error("OTP verification error:", error);
      setError(error.message || "Authentication failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const oldCartItems = cartItems;
      const result = await signInWithPopup(auth, provider);
      await createUserInFirestore(result.user);
      await recreateCartForUser(result.user, oldCartItems);
      setCurrentModal(null);
    } catch (error) {
      setError(error.message);
    }
    // analytics
    logAnalyticsEvent("sign_in_google", {
      method: "google",
      environment: environment,
    });
  };

  return (
    <div css={{ padding: "0 10px", maxWidth: "1140px", margin: "0 auto" }}>
      <div css={{ height: "50px" }} />
      <a
        href="/"
        css={{ cursor: "pointer", display: "flex", justifyContent: "center" }}
      >
        <img
          src="/images/fullproof_logo_black.svg"
          alt="Fullproof"
          css={{
            height: "40px",
            marginBottom: 10,
            [mq[0]]: { height: "50px" },
          }}
        />
      </a>
      <div css={{ height: "40px" }} />
      <div css={{ maxWidth: "325px", margin: "0 auto" }}>
        {error && (
          <p className="error">
            {error.includes("user-not-found")
              ? "User not found. Please check your email."
              : error.includes("wrong-password")
                ? "Wrong password. Please try again."
                : error.includes("email-already-in-use")
                  ? "Email already in use. Please try again."
                  : error.includes("invalid-email")
                    ? "Invalid email. Please try again."
                    : error.includes("too-many-requests")
                      ? "Too many requests. Please try again later."
                      : error.includes("invalid-credential")
                        ? "Invalid credentials. Please try again."
                        : error.includes("missing-credential")
                          ? "Missing credentials. Please try again."
                          : error.includes("user-disabled")
                            ? "User disabled. Please contact support."
                            : error.includes("missing-password")
                              ? "Missing password. Please try again."
                              : error}
          </p>
        )}
        <div
          css={{
            fontSize: "1.2rem",
            fontWeight: 600,
            paddingBottom: 10,
            color: "black",
            [mq[0]]: { fontSize: "1.5rem" },
          }}
        >
          {step === 1 ? "Sign In" : "Check your email"}
        </div>

        <form
          className="signin-form"
          onSubmit={(e) =>
            step === 1 ? handleRequestOtp(e) : handleVerifyOtp(e)
          }
        >
          {step === 1 ? (
            <>
              <label css={{ margin: 0, color: "black" }}>Email</label>
              <input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                css={{ margin: 0, marginBottom: 10 }}
                required
                disabled={loading}
              />
            </>
          ) : (
            <>
              <p
                css={{
                  margin: "0 0 15px 0",
                  fontSize: ".85rem",
                  color: "#666",
                  lineHeight: 1.4,
                  [mq[0]]: { fontSize: ".95rem" },
                }}
              >
                We're sending a 6-digit code to your email. You should receive it within a minute. Enter it below before it
                expires.
              </p>
              <label css={{ margin: 0, color: "black" }}>
                One-Time Password
              </label>
              <input
                type="text"
                placeholder="Enter 6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                css={{ margin: 0 }}
                maxLength={6}
                pattern="[0-9]{6}"
                required
                disabled={loading}
              />
              <p
                css={{
                  margin: "15px 0 10px 0",
                  fontSize: ".84rem",
                  color: "#666",
                  [mq[0]]: { fontSize: ".89rem" },
                }}
              >
                <span
                  onClick={() => {
                    setStep(1);
                    setOtp("");
                    setError("");
                  }}
                  css={{
                    cursor: "pointer",
                    color: "blue",
                    textDecoration: "underline",
                  }}
                >
                  Change email
                </span>
                {" | "}
                <span
                  onClick={handleResendOtp}
                  css={{
                    cursor: "pointer",
                    color: "blue",
                    textDecoration: "underline",
                  }}
                >
                  {loading ? "Sending..." : "Send again"}
                </span>
              </p>
            </>
          )}
          <button
            type="submit"
            className="btn btn-primary"
            css={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 100,
              "&:disabled": {
                opacity: 0.6,
                cursor: "not-allowed",
              },
            }}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} css={{ color: "white" }} />
            ) : step === 1 ? (
              "Next"
            ) : (
              "Sign In"
            )}
          </button>
          {extendedLoading && (
            <p
              css={{
                margin: "15px 0 0 0",
                fontSize: ".85rem",
                color: "#666",
                textAlign: "center",
                animation: "fadeIn 0.5s ease-in",
                "@keyframes fadeIn": {
                  from: { opacity: 0 },
                  to: { opacity: 1 },
                },
              }}
            >
              Sending your code securely...
            </p>
          )}
          <input type="submit" css={{ display: "none" }} />
        </form>

        {step === 1 && (
          <button
            className="btn btn-secondary"
            onClick={handleGoogleSignIn}
            css={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 100,
              background: "white",
              border: "1px solid black",
            }}
          >
            <img
              alt="Google logo"
              src="/images/google_logo.png"
              css={{
                height: "18px",
                top: "0px",
                marginRight: "8px",
                position: "relative",
              }}
            />{" "}
            Sign in with Google
          </button>
        )}

        {/* Temporarly removed until Apple SignIn is fixed */}
        {/* <button
        className="btn btn-secondary"
        onClick={handleAppleSignIn}
        css={{ display: "flex", justifyContent: "center", alignItems: "center", borderRadius: 100, background: "white", border: "1px solid black" }}
      >
        <img alt="Apple logo" src="/images/apple_logo.png" css={{ height: "18px", top: "0px", marginRight: "8px", position: "relative" }} />
        Sign in with Apple
      </button> */}

        <p className="footer-text">
          By creating an account you agree to our{" "}
          <a href="/legal/terms-of-service">terms of service</a>.
        </p>

        <p
          className="footer-text"
          onClick={() =>
            // close modal and navigate to homepage
            setCurrentModal(null) & navigate(`/`)
          }
          css={{ cursor: "pointer" }}
        >
          Back to <u>Homepage</u>.
        </p>
      </div>
    </div>
  );
};

export default SignIn;
